import React, { createContext, useContext, useEffect } from 'react';

type ComponentSize = 'sm' | 'md' | 'lg';

interface NulaConfig {
  /** Default size for all components */
  componentSize?: ComponentSize;
  /** Theme mode */
  theme?: 'light' | 'dark';
  /** Token overrides — applied as CSS variables on the provider root */
  tokenOverrides?: Record<string, string>;
}

const ConfigContext = createContext<NulaConfig>({});

/**
 * useConfig — access the nearest ConfigProvider values.
 */
export function useConfig(): NulaConfig {
  return useContext(ConfigContext);
}

interface ConfigProviderProps extends NulaConfig {
  children: React.ReactNode;
}

/**
 * ConfigProvider — global configuration for all Nula components.
 *
 * Wraps your app (or a subtree) to set defaults for size, theme, and tokens.
 * Nested ConfigProviders merge with their parent — inner values override outer.
 *
 * | Prop | Type | Default | Description |
 * |------|------|---------|-------------|
 * | componentSize | 'sm' \| 'md' \| 'lg' | 'md' | Default size for all components |
 * | theme | 'light' \| 'dark' | — | Sets `data-theme` on the wrapper |
 * | tokenOverrides | Record<string, string> | — | CSS variable overrides |
 *
 * @example
 * <ConfigProvider componentSize="sm" theme="dark">
 *   <App />
 * </ConfigProvider>
 *
 * // Override specific tokens for a section
 * <ConfigProvider tokenOverrides={{ '--color-accent': '#E11D48' }}>
 *   <SpecialSection />
 * </ConfigProvider>
 */
export function ConfigProvider({ componentSize, theme, tokenOverrides, children }: ConfigProviderProps) {
  const parent = useConfig();

  const merged: NulaConfig = {
    componentSize: componentSize ?? parent.componentSize ?? 'md',
    theme: theme ?? parent.theme,
    tokenOverrides: { ...parent.tokenOverrides, ...tokenOverrides },
  };

  // Apply theme to documentElement when at root level
  useEffect(() => {
    if (theme) {
      document.documentElement.setAttribute('data-theme', theme);
    }
  }, [theme]);

  // Convert token overrides to inline CSS variables
  const cssVars: React.CSSProperties = {};
  if (tokenOverrides) {
    for (const [key, val] of Object.entries(tokenOverrides)) {
      (cssVars as Record<string, string>)[key] = val;
    }
  }

  return (
    <ConfigContext.Provider value={merged}>
      <div style={Object.keys(cssVars).length > 0 ? cssVars : undefined} data-theme={theme || undefined}>
        {children}
      </div>
    </ConfigContext.Provider>
  );
}
